import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Archive as ArchiveIcon, 
  ArrowLeft,
  Trash2,
  RotateCcw,
  Clock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const Archive = ({ user }) => {
  const [archivedCases, setArchivedCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArchivedCases();
  }, []);

  const fetchArchivedCases = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/archived`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setArchivedCases(data);
      } else {
        toast.error('Fehler beim Laden der archivierten Akten');
      }
    } catch (error) {
      console.error('Error fetching archived cases:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysRemaining = (archivedAt) => {
    const archived = new Date(archivedAt);
    const now = new Date();
    const daysPassed = Math.floor((now - archived) / (1000 * 60 * 60 * 24));
    return Math.max(0, 14 - daysPassed);
  };

  const restoreCase = async (caseId) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Akte erfolgreich wiederhergestellt');
        fetchArchivedCases();
      } else {
        toast.error('Fehler beim Wiederherstellen der Akte');
      }
    } catch (error) {
      console.error('Error restoring case:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const permanentlyDelete = async (caseId) => {
    if (!window.confirm('Diese Akte wird endgültig gelöscht und kann nicht wiederhergestellt werden. Fortfahren?')) {
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}/permanent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Akte endgültig gelöscht');
        fetchArchivedCases();
      } else {
        toast.error('Fehler beim Löschen der Akte');
      }
    } catch (error) {
      console.error('Error deleting case:', error);
      toast.error('Verbindungsfehler');
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/cases" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zu Akten
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Archiv</h1>
                <p className="text-gray-600 mt-2">
                  Gelöschte Akten werden 14 Tage archiviert
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-700">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">{archivedCases.length} archivierte Akten</span>
                </div>
              </div>
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex items-start">
              <Clock className="w-5 h-5 text-yellow-700 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-yellow-700">
                  <strong>Automatische Löschung:</strong> Akten im Archiv werden nach 14 Tagen automatisch und endgültig gelöscht.
                  Sie können Akten jederzeit wiederherstellen oder manuell löschen.
                </p>
              </div>
            </div>
          </div>

          {/* Archived Cases List */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200" style={{borderTopColor: '#0A4D8C'}}></div>
            </div>
          ) : archivedCases.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <ArchiveIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Keine archivierten Akten</h3>
              <p className="text-gray-600">Das Archiv ist leer.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktennummer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kunde
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fahrzeug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archiviert am
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verbleibende Tage
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {archivedCases.map((caseItem) => {
                    const daysRemaining = calculateDaysRemaining(caseItem.archived_at);
                    return (
                      <tr key={caseItem._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {caseItem.case_number}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{caseItem.customer_name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {caseItem.vehicle?.manufacturer} {caseItem.vehicle?.model}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {new Date(caseItem.archived_at).toLocaleDateString('de-DE')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            daysRemaining <= 3 
                              ? 'bg-red-100 text-red-800'
                              : daysRemaining <= 7
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            <Clock className="w-4 h-4 mr-1" />
                            {daysRemaining} Tage
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => restoreCase(caseItem._id)}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                            title="Wiederherstellen"
                          >
                            <RotateCcw className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => permanentlyDelete(caseItem._id)}
                            className="text-red-600 hover:text-red-900"
                            title="Endgültig löschen"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </Layout>
  );
};

export default Archive;
